/**
 * `src/lib/ui/nav/nav.svelte.ts` — the navigation's runtime: the history
 * mirror in `$state`, the back control's target, the active tab, the pending
 * tint and the route transitions.
 *
 * Every decision is made by the pure functions in `nav-model.ts`; this module
 * only feeds them from Kit's three navigation hooks and exposes the results as
 * read-only getters on `nav`. `installNavigation()` registers those hooks, so it
 * must run during `+layout.svelte`'s initialisation, once.
 *
 * WHAT HAPPENS ON A NAVIGATION, in Kit's order:
 *
 *   beforeNavigate  a link click marks its `<a>` with `data-pending`, so the
 *                   pressed tint holds while `load` runs (no spinner, no bar).
 *   onNavigate      the navigation is classified (push / pop / fade / none) and
 *                   the view transition, if any, starts.
 *   afterNavigate   the tint clears and the mirror records the new entry, with
 *                   its kind, even when no transition ran: back inverts it.
 */

import { afterNavigate, beforeNavigate, goto, onNavigate } from '$app/navigation';
import { page } from '$app/state';
import { onMount, untrack } from 'svelte';

import { azkarHref } from '$lib/azkar/href';
import { tasbihHref } from '$lib/tasbih/href';
import { loadReadingPosition } from '$lib/ui/reader/position';

import { bookmarksHref, homeHref, readIndexHref, salahHref, settingsHref } from './links';
import {
	EMPTY_STACK,
	backTarget as backTargetOf,
	classify,
	current,
	reduce,
	routeInfo,
	tabFor,
	withCurrentHref,
	type NavKind,
	type NavStack,
	type TabId
} from './nav-model';
import { runTransition } from './transition';

export interface ResolvedBackTarget {
	mode: 'history' | 'up';
	href: string;
	/** The destination's name, shown beside the chevron and in the link's
	 *  accessible name, so the label always names where the tap goes. */
	label: string;
}

let stack = $state.raw<NavStack>(EMPTY_STACK);
/** Back labels the pages registered, by pathname + search («سورة البقرة»). */
const titles = $state<Record<string, string>>({});
let readingPlace = $state<{ surah: number; ayah: number } | null>(null);

/* Per-navigation scratch, consumed and reset by `afterNavigate`. */
let pendingKind: NavKind | null = null;
let intent: 'pop' | null = null;
let replaceNext = false;
/* A second tap on a history-mode back control before the first has landed
 * would go back TWO entries. Latched until the landing, or a second at most. */
let backingUntil = 0;

function hrefOf(url: URL): string {
	return url.pathname + url.search;
}

/**
 * What the address bar says, pathname + search. The mirror records THIS, not
 * Kit's `to.url`: after a shallow `replaceState` (the search page's `?q=`),
 * Kit navigates a back to that entry to the URL the entry was OPENED with
 * (`/search`), while the address bar, and so the entry, still says
 * `/search?q=…`. For every other navigation the two are the same.
 */
function addressHref(fallback: URL): string {
	return typeof location === 'undefined' ? hrefOf(fallback) : location.pathname + location.search;
}

/** The href of an up target. Static routes only (`UP_TARGETS`), each through
 *  its `resolve()` helper. */
function upHref(route: string): string {
	switch (route) {
		case '/':
			return homeHref();
		case '/read':
			return readIndexHref();
		case '/salah':
			return salahHref();
		case '/bookmarks':
			return bookmarksHref();
		case '/settings':
			return settingsHref();
		case '/azkar':
			return azkarHref();
		case '/tasbih':
			return tasbihHref();
		default:
			return homeHref();
	}
}

function clearPending(): void {
	if (typeof document === 'undefined') return;
	document.querySelectorAll('[data-pending]').forEach((el) => el.removeAttribute('data-pending'));
}

/** The clicked link's `data-nav`, read from the event itself: Kit skips
 *  `beforeNavigate` while another navigation is in flight, so a value stored
 *  there could belong to the previous tap. */
function linkNavOf(event: Event | undefined): string | null {
	const target = event?.target;
	if (!(target instanceof Element)) return null;
	return target.closest('[data-nav]')?.getAttribute('data-nav') ?? null;
}

export const nav = {
	/** The lit tab: the current entry's. In the gap before `afterNavigate`
	 *  records the new page, it is worked out from the entry we came from. */
	get tab(): TabId {
		const top = current(stack);
		const route = page.route.id;
		return top && top.route === route ? top.tab : tabFor(route, top?.tab ?? null);
	},

	/** Where the back control goes; `null` on a tab root. */
	get backTarget(): ResolvedBackTarget | null {
		const target = backTargetOf(stack, page.route.id);
		if (!target) return null;
		if (target.mode === 'history') {
			return {
				mode: 'history',
				href: target.href,
				label: titles[target.href] ?? routeInfo(target.route).title ?? 'السابق'
			};
		}
		return {
			mode: 'up',
			href: upHref(target.route),
			label: routeInfo(target.route).title ?? 'السابق'
		};
	},

	/** Where the Read tab continues from. Seeded from the stored position on
	 *  mount, then kept current by the reader. */
	get readingPlace(): { surah: number; ayah: number } | null {
		return readingPlace;
	}
};

export function installNavigation(): void {
	beforeNavigate((n) => {
		if (n.type !== 'link' || n.willUnload) return;
		const target = n.event.target;
		const link = target instanceof Element ? target.closest('a') : null;
		if (!link) return;
		link.setAttribute('data-pending', '');
		n.complete.then(clearPending, clearPending);
	});

	onNavigate((n) => {
		const popped = n.type === 'popstate' ? n : null;
		const event = popped?.event as
			(PopStateEvent & { hasUAVisualTransition?: boolean }) | undefined;
		const kind = classify({
			type: n.type,
			from: n.from && { route: n.from.route.id, href: hrefOf(n.from.url) },
			to: n.to && { route: n.to.route.id, href: hrefOf(n.to.url) },
			willUnload: n.willUnload,
			delta: popped ? popped.delta : 0,
			uaVisual: event?.hasUAVisualTransition === true,
			/* Only our own `goto` can carry the up intent; a link tapped after an
			 * up-navigation was aborted must not inherit it. */
			intent: n.type === 'goto' ? intent : null,
			linkNav: n.type === 'link' ? linkNavOf(n.event) : null,
			stack
		});
		pendingKind = kind;
		return runTransition(n, kind);
	});

	afterNavigate((n) => {
		clearPending();
		backingUntil = 0;
		const route = n.to?.route.id ?? null;
		const href = addressHref(n.to?.url ?? page.url);
		if (n.type === 'enter') {
			stack = reduce(stack, { type: 'enter', route, href });
		} else if (n.type === 'popstate') {
			stack = reduce(stack, {
				type: 'popstate',
				route,
				href,
				delta: n.delta,
				kind: pendingKind ?? 'none'
			});
		} else if (n.type === 'goto' || n.from?.url.href !== n.to?.url.href) {
			/* A link to the very URL already shown makes Kit REPLACE the entry
			 * (`url.href === location.href`), so there is nothing to mirror. */
			stack = reduce(stack, {
				type: 'push',
				route,
				href,
				kind: pendingKind ?? 'push',
				replace: n.type === 'goto' && replaceNext
			});
		}
		pendingKind = null;
		intent = null;
		replaceNext = false;
	});

	onMount(() => {
		/* Kit fires the `enter` navigation after mount in SPA mode; this only
		 * covers a start that never reaches us, so the first screen is known. */
		if (stack.index < 0) {
			stack = reduce(stack, { type: 'enter', route: page.route.id, href: hrefOf(page.url) });
		}
		/* Only if the reader has not already said where it is: a cold deep link
		 * into the reader reports its place before this read resolves. */
		void loadReadingPosition().then((position) => {
			if (position && readingPlace === null) {
				readingPlace = { surah: position.surah, ayah: position.ayah };
			}
		});
		/* A pop the mirror cannot see — an entry that differs only by its
		 * `#fragment`, which Kit handles without a navigation — lands on the
		 * same href as the mirror's top, and no `afterNavigate` comes to clear
		 * the back control's latch. Clear it here, or the next press is
		 * swallowed for a second. A real pop lands elsewhere and keeps the
		 * latch until `afterNavigate`. */
		const onPop = () => {
			if (location.pathname + location.search === current(stack)?.href) backingUntil = 0;
		};
		window.addEventListener('popstate', onPop);
		return () => window.removeEventListener('popstate', onPop);
	});
}

/**
 * The back control's action. HISTORY mode is `history.back()`: Kit restores
 * that page's scroll. UP mode REPLACES the current entry with the parent, so
 * the screen being left does not stay behind for the system back to reopen.
 * `upHref()` builds every href through a `resolve()` helper.
 */
export function goBack(): void {
	const target = nav.backTarget;
	if (!target) return;
	if (target.mode === 'history') {
		const now = Date.now();
		if (now < backingUntil) return;
		backingUntil = now + 1000;
		history.back();
		return;
	}
	intent = 'pop';
	replaceNext = true;
	goto(target.href, { replaceState: true }).catch(() => {
		intent = null;
		replaceNext = false;
	});
}

/**
 * The search page's shallow `replaceState` changed the address bar without a
 * navigation; copy it into the mirror's current entry, so a history-mode back
 * control pointing here carries the query in its href.
 */
export function recordShallowReplace(): void {
	if (typeof location === 'undefined') return;
	stack = withCurrentHref(stack, page.route.id, location.pathname + location.search);
}

/** Register the back label for the current URL. PageHeader and the reader
 *  call it from an `$effect`, so it follows the URL. */
export function setPageTitle(title: string): void {
	titles[hrefOf(page.url)] = title;
}

/** The reader's `onPlace`: the Read tab now continues from here. */
export function setReadingPlace(surah: number, ayah: number): void {
	/* Untracked, so a caller inside an effect does not come to depend on it. */
	const place = untrack(() => readingPlace);
	if (place?.surah === surah && place.ayah === ayah) return;
	readingPlace = { surah, ayah };
}
